import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "wouter";
import { useI18n, useLocalized } from "@/lib/i18n";
import { ChevronDown } from "lucide-react";
import { useCurrency } from "@/lib/currency";
import { useTheme } from "@/lib/theme-context";

const BASE_URL = import.meta.env.BASE_URL || "/";

interface Category {
  id: number;
  name: string;
  nameAr?: string | null;
  slug: string;
  parentId?: number | null;
  imageUrl?: string | null;
  productCount?: number;
  children?: Category[];
}

export function MegaMenu({ inline = false }: { inline?: boolean }) {
  const { lang } = useI18n();
  const l = useLocalized();
  const { formatPrice } = useCurrency();
  const theme = useTheme();
  const [categories, setCategories] = useState<Category[]>([]);
  const [activeMenu, setActiveMenu] = useState<number | null>(null);
  const [featuredProducts, setFeaturedProducts] = useState<any[]>([]);
  const menuRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const triggerRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const panelRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  const megaMenuEnabled = theme?.megaMenuEnabled !== "false";
  const showFeatured = theme?.megaMenuShowFeatured !== "false";
  const showImages = theme?.megaMenuShowImages !== "false";
  const megaMenuBg = theme?.megaMenuBg || "#fff";
  const megaMenuPosition = theme?.megaMenuPosition || "below";
  const megaMenuCols = parseInt(theme?.megaMenuColumns || "4") || 4;
  const showCount = theme?.megaMenuShowCount !== "false";

  const hiddenIds: number[] = (() => {
    try { return JSON.parse(theme?.megaMenuHiddenCategories || "[]"); } catch { return []; }
  })();

  const categoryOrder: number[] = (() => {
    try { return JSON.parse(theme?.megaMenuCategoryOrder || "[]"); } catch { return []; }
  })();

  useEffect(() => {
    fetch(`${BASE_URL}api/categories`)
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) setCategories(buildTree(data));
      })
      .catch(() => {});

    fetch(`${BASE_URL}api/products?featured=true&limit=4&fields=card`)
      .then((r) => r.json())
      .then((data) => {
        const prods = data?.products || data || [];
        if (Array.isArray(prods)) setFeaturedProducts(prods.slice(0, 4));
      })
      .catch(() => {});
  }, []);

  function buildTree(flat: Category[]): Category[] {
    const map = new Map<number, Category>();
    const roots: Category[] = [];
    flat.forEach((c) => map.set(c.id, { ...c, children: [] }));
    flat.forEach((c) => {
      const item = map.get(c.id)!;
      if (c.parentId && map.has(c.parentId)) {
        map.get(c.parentId)!.children!.push(item);
      } else {
        roots.push(item);
      }
    });
    return roots;
  }

  const startCloseTimer = useCallback(() => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      setActiveMenu(null);
    }, 150);
  }, []);

  const cancelCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = undefined;
    }
  }, []);

  const handleTriggerEnter = useCallback((catId: number) => {
    cancelCloseTimer();
    setActiveMenu(catId);
  }, [cancelCloseTimer]);

  useEffect(() => {
    if (activeMenu === null) return;
    const closeOnScroll = () => {
      cancelCloseTimer();
      setActiveMenu(null);
    };
    window.addEventListener("scroll", closeOnScroll, { passive: true });
    return () => window.removeEventListener("scroll", closeOnScroll);
  }, [activeMenu, cancelCloseTimer]);

  const handleTriggerLeave = useCallback(() => {
    startCloseTimer();
  }, [startCloseTimer]);

  const handlePanelEnter = useCallback(() => {
    cancelCloseTimer();
  }, [cancelCloseTimer]);

  const handlePanelLeave = useCallback(() => {
    startCloseTimer();
  }, [startCloseTimer]);

  const handleClose = useCallback(() => {
    cancelCloseTimer();
    setActiveMenu(null);
  }, [cancelCloseTimer]);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (!menuRef.current?.contains(target)) {
        let clickedOnPanel = false;
        panelRefs.current.forEach((el) => {
          if (el?.contains(target)) clickedOnPanel = true;
        });
        if (!clickedOnPanel) {
          handleClose();
        }
      }
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [handleClose]);

  if (!megaMenuEnabled || categories.length === 0) return null;

  if (inline && megaMenuPosition !== "inline" && megaMenuPosition !== "above") return null;
  if (!inline && (megaMenuPosition === "inline" || megaMenuPosition === "above")) return null;

  // Combine the theme-level "hidden" list (legacy, per-theme JSON) with the
  // per-category `hideFromDesktopMenu` / `hideFromMobileMenu` flag set on the
  // category itself. Both are honored so existing stores keep working AND
  // merchants get per-section visibility control out of the admin without
  // touching theme JSON. The filter walks the whole tree (root + children +
  // sub-children) so a hidden subcategory disappears from every dropdown
  // panel, not just the top-level row.
  const isMobileViewport =
    typeof window !== "undefined" && window.matchMedia("(max-width: 1279px)").matches;
  const isHidden = (c: Category): boolean => {
    if (hiddenIds.includes(c.id)) return true;
    if (isMobileViewport && (c as any).hideFromMobileMenu === true) return true;
    if (!isMobileViewport && (c as any).hideFromDesktopMenu === true) return true;
    return false;
  };
  // Auto-hide empty categories: a node with productCount === 0 and no
  // surviving children disappears from the menu. As soon as a product is
  // assigned, the category reappears automatically. We only hide when
  // productCount is explicitly 0 — undefined means "count not loaded yet",
  // so the category stays visible (preserves SSR/no-count behavior).
  // Merchants retain manual override via hideFromDesktopMenu /
  // hideFromMobileMenu (handled above in isHidden).
  const pruneTree = (nodes: Category[]): Category[] =>
    nodes
      .filter((n) => !isHidden(n))
      .map((n) => ({ ...n, children: n.children ? pruneTree(n.children) : [] }))
      .filter((n) => {
        const hasChildren = (n.children?.length ?? 0) > 0;
        const isEmpty = n.productCount === 0;
        return hasChildren || !isEmpty;
      });
  const visibleCategories = pruneTree(categories);
  const sortedVisible = categoryOrder.length > 0
    ? [...visibleCategories].sort((a, b) => {
        const ai = categoryOrder.indexOf(a.id);
        const bi = categoryOrder.indexOf(b.id);
        if (ai === -1 && bi === -1) return 0;
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      })
    : visibleCategories;
  const parentCategories = sortedVisible.filter(c => c.children && c.children.length > 0);
  const standaloneCategories = sortedVisible.filter(c => !c.children || c.children.length === 0);
  const isDropdownOpen = activeMenu !== null && parentCategories.some(c => c.id === activeMenu && c.children && c.children.length > 0);

  if (inline) {
    return (
      <div ref={menuRef} className="flex items-center justify-center gap-0 relative" style={{ zIndex: 9999 }}>
        {parentCategories.map((cat) => {
          const isActive = activeMenu === cat.id;
          const hasChildren = cat.children && cat.children.length > 0;
          return (
            <div
              key={cat.id}
              className="relative"
              style={{ zIndex: 9999 }}
              ref={(el) => { if (el) triggerRefs.current.set(cat.id, el); }}
              onMouseEnter={() => handleTriggerEnter(cat.id)}
              onMouseLeave={handleTriggerLeave}
            >
              <button
                className={`flex items-center gap-1 px-3 py-2.5 text-sm font-semibold tracking-[0.05em] uppercase transition-all whitespace-nowrap ${
                  isActive ? "text-black dark:text-white" : "text-gray-700 hover:text-black dark:hover:text-white"
                }`}
              >
                {l(cat, "name", "nameAr")}
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isActive ? "rotate-180" : ""}`} />
              </button>

              {isActive && hasChildren && (
                <>
                  <div className="absolute top-full left-0 right-0 h-2" />
                  <div
                    ref={(el) => { if (el) panelRefs.current.set(cat.id, el); }}
                    className="absolute z-[9999] bg-white border border-gray-200 shadow-xl min-w-[600px]"
                    style={{
                      top: "100%",
                      backgroundColor: megaMenuBg,
                      ...(lang === "ar" ? { right: 0, left: "auto" } : { left: 0, right: "auto" })
                    }}
                    onMouseEnter={handlePanelEnter}
                    onMouseLeave={handlePanelLeave}
                  >
                    <DropdownContent
                      cat={cat} lang={lang} l={l} megaMenuCols={megaMenuCols}
                      showImages={showImages} showCount={showCount} showFeatured={showFeatured}
                      featuredProducts={featuredProducts} formatPrice={formatPrice}
                      closeMenu={handleClose}
                    />
                  </div>
                </>
              )}
            </div>
          );
        })}

        {standaloneCategories.map((cat) => (
          <Link
            key={cat.id}
            href={`/shop?category=${cat.id}`}
            className="px-3 py-2.5 text-sm font-semibold tracking-[0.05em] uppercase text-gray-700 hover:text-black dark:hover:text-white transition-colors whitespace-nowrap"
          >
            {l(cat, "name", "nameAr")}
          </Link>
        ))}

        <Link
          href="/blog"
          className="px-3 py-2.5 text-sm font-semibold tracking-[0.05em] uppercase text-gray-700 hover:text-black dark:hover:text-white transition-colors whitespace-nowrap"
        >
          {lang === "ar" ? "المدونة" : "Blog"}
        </Link>

        {isDropdownOpen && (
          <div
            className="fixed inset-0 bg-black/30 pointer-events-none"
            style={{ zIndex: 9997 }}
          />
        )}
      </div>
    );
  }

  return (
    <>
      {isDropdownOpen && (
        <div
          className="fixed inset-0 bg-black/30 pointer-events-none"
          style={{ zIndex: 9997 }}
        />
      )}
      <div ref={menuRef} className="hidden xl:block border-b border-gray-100 relative" style={{ backgroundColor: megaMenuBg, zIndex: 9999 }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-0">
            {parentCategories.map((cat) => {
              const isActive = activeMenu === cat.id;
              const hasChildren = cat.children && cat.children.length > 0;
              return (
                <div
                  key={cat.id}
                  className="relative"
                  ref={(el) => { if (el) triggerRefs.current.set(cat.id, el); }}
                  onMouseEnter={() => handleTriggerEnter(cat.id)}
                  onMouseLeave={handleTriggerLeave}
                >
                  <button
                    className={`flex items-center gap-1 px-4 py-2.5 text-sm font-semibold tracking-[0.08em] uppercase transition-all whitespace-nowrap ${
                      isActive ? "text-black" : "text-gray-700 hover:text-black"
                    }`}
                  >
                    {l(cat, "name", "nameAr")}
                    <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isActive ? "rotate-180" : ""}`} />
                  </button>
                  {isActive && hasChildren && (
                    <div
                      ref={(el) => { if (el) panelRefs.current.set(cat.id, el); }}
                      className="fixed left-0 right-0 z-[9999] pointer-events-none"
                      style={{ top: menuRef.current ? `${menuRef.current.getBoundingClientRect().bottom}px` : undefined }}
                      onMouseEnter={handlePanelEnter}
                      onMouseLeave={handlePanelLeave}
                    >
                      <div
                        className="max-w-7xl mx-auto bg-white border-b border-x border-gray-200 shadow-xl pointer-events-auto"
                        style={{ backgroundColor: megaMenuBg }}
                      >
                        <DropdownContent
                          cat={cat} lang={lang} l={l} megaMenuCols={megaMenuCols}
                          showImages={showImages} showCount={showCount} showFeatured={showFeatured}
                          featuredProducts={featuredProducts} formatPrice={formatPrice}
                          closeMenu={handleClose} fullWidth
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {standaloneCategories.slice(0, 4).map((cat) => (
              <Link
                key={cat.id}
                href={`/shop?category=${cat.id}`}
                className="px-4 py-2.5 text-sm font-semibold tracking-[0.08em] uppercase text-gray-700 hover:text-black transition-colors whitespace-nowrap"
              >
                {l(cat, "name", "nameAr")}
              </Link>
            ))}

            <Link
              href="/blog"
              className="px-4 py-2.5 text-sm font-semibold tracking-[0.08em] uppercase text-gray-700 hover:text-black transition-colors whitespace-nowrap"
            >
              {lang === "ar" ? "المدونة" : "Blog"}
            </Link>

            <Link
              href="/shop"
              className="px-4 py-2.5 text-sm font-semibold tracking-[0.08em] uppercase text-gray-700 hover:text-black transition-colors whitespace-nowrap"
              aria-label={lang === "ar" ? "عرض كل المنتجات" : "Shop all products"}
            >
              {lang === "ar" ? "عرض الكل" : "Shop All"}
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}

function DropdownContent({
  cat, lang, l, megaMenuCols, showImages, showCount, showFeatured,
  featuredProducts, formatPrice, closeMenu, fullWidth
}: {
  cat: Category; lang: string; l: any; megaMenuCols: number;
  showImages: boolean; showCount: boolean; showFeatured: boolean;
  featuredProducts: any[]; formatPrice: (n: number) => string;
  closeMenu: () => void; fullWidth?: boolean;
}) {
  const px = fullWidth ? "max-w-7xl mx-auto px-8 py-8" : "px-8 py-6";
  const gapX = fullWidth ? "gap-x-10" : "gap-x-8";
  const imgSize = fullWidth ? "w-7 h-7" : "w-6 h-6";
  const subImgSize = fullWidth ? "w-5 h-5" : "w-4 h-4";

  return (
    <div className={px}>
      <div className="grid grid-cols-12 gap-6">
        <div className={`${showFeatured && featuredProducts.length > 0 ? "col-span-8" : "col-span-12"}`}>
          <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-100">
            <Link
              href={`/shop?category=${cat.id}`}
              className="text-base font-bold tracking-[0.1em] uppercase text-black hover:opacity-70 transition-opacity"
              onClick={closeMenu}
            >
              {l(cat, "name", "nameAr")}
            </Link>
            <Link
              href={`/shop?category=${cat.id}`}
              className="text-xs tracking-wider uppercase text-gray-500 hover:text-black transition-colors px-2 py-2 inline-block min-h-[44px]"
              onClick={closeMenu}
              aria-label={lang === "ar" ? `عرض كل: ${l(cat, "name", "nameAr")}` : `View all: ${l(cat, "name", "nameAr")}`}
            >
              {lang === "ar" ? "عرض الكل →" : "View All →"}
            </Link>
          </div>
          <div className={`grid ${gapX} gap-y-1`} style={{ gridTemplateColumns: `repeat(${Math.min(megaMenuCols, (cat.children || []).length)}, 1fr)` }}>
            {(cat.children || []).map((sub) => (
              <div key={sub.id} className="mb-3">
                <Link
                  href={`/shop?category=${sub.id}`}
                  className="flex items-center gap-2 text-sm font-semibold tracking-wide text-gray-900 hover:text-black transition-colors mb-1.5 pb-1 border-b border-transparent hover:border-gray-200"
                  onClick={closeMenu}
                >
                  {showImages && sub.imageUrl && (
                    <img src={sub.imageUrl} alt={l(sub, "name", "nameAr")} className={`${imgSize} rounded object-cover shrink-0`} />
                  )}
                  <span>{l(sub, "name", "nameAr")}</span>
                  {showCount && sub.productCount !== undefined && sub.productCount > 0 && (
                    <span className="text-xs text-gray-500 font-normal">({sub.productCount})</span>
                  )}
                </Link>
                {sub.children && sub.children.length > 0 && (
                  <div className="ps-0 space-y-1">
                    {sub.children.map((subsub) => (
                      <Link
                        key={subsub.id}
                        href={`/shop?category=${subsub.id}`}
                        className="flex items-center gap-1.5 text-[13px] text-gray-700 hover:text-black transition-colors py-0.5 hover:ps-1 duration-200"
                        onClick={closeMenu}
                      >
                        {showImages && subsub.imageUrl && (
                          <img src={subsub.imageUrl} alt={l(subsub, "name", "nameAr")} className={`${subImgSize} rounded object-cover shrink-0`} />
                        )}
                        <span>{l(subsub, "name", "nameAr")}</span>
                        {showCount && subsub.productCount !== undefined && subsub.productCount > 0 && (
                          <span className="text-xs text-gray-500">({subsub.productCount})</span>
                        )}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        {showFeatured && featuredProducts.length > 0 && (
          <div className="col-span-4 border-s border-gray-100 ps-6">
            <h4 className="text-xs tracking-[0.15em] uppercase text-gray-500 font-semibold mb-3">
              {lang === "ar" ? "منتجات مميزة" : "Featured Products"}
            </h4>
            <div className="grid grid-cols-2 gap-2">
              {featuredProducts.slice(0, 4).map((prod) => (
                <Link key={prod.id} href={`/product/${prod.id}`} className="group/prod" onClick={closeMenu}>
                  {showImages && (
                    <div className="aspect-square bg-gray-50 overflow-hidden mb-1">
                      {prod.imageUrl ? (
                        <img src={prod.imageUrl} alt={l(prod, "name")} className="w-full h-full object-cover group-hover/prod:scale-105 transition-transform duration-300" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-300 text-[10px]"></div>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-gray-700 leading-tight line-clamp-1 group-hover/prod:text-black transition-colors">{l(prod, "name")}</p>
                  <p className="text-xs font-semibold mt-0.5">
                    {prod.salePrice ? <span className="text-red-600">{formatPrice(prod.salePrice)}</span> : formatPrice(prod.price)}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
